/**
 * idleTimer.js
 *
 * @description This will trigger the events when the page is going to idle state and the page
 * is getting active after idle
 *
 * Steps:
 *  1. Intialize the timer using <code> idleTimer.init(); </code>
 *  2. Listen for events for example
 *      <code> idleTimer.on('idle', () => { // Call back to handle idle event }); </code>
 *      <code> idleTimer.on('active', () => { // Call back to handle active event }); </code>
 *  3. If the process should be stoped use <code> idleTimer.pause(); </code>
 *  4. To resume the process use <code> idleTimer.resume(); </code>
 */
import EventEmitter from 'events';
const DEFAULT_EVENTS = ['mousemove', 'scroll', 'keydown', 'resize', 'touchstart', 'touchmove', 'wheel'];
const DEFAULT_INTERVAL = 1000 * 60 * 5; // 5 Minutes
const DEFAULT_TIMEOUT = 1000 * 60 * 5; // 5 Minutes

class IdleTimer extends EventEmitter {
  constructor() {
    var _this;
    super();
    _this = this;
    /**
     * @description Initializes the process of checking for idle state
     * @param {Number} - interval - Time interval the idle check should iterate (in milliseconds)
     * @param {Number} - maxTimeout - Time limit to consider that the page is idle (in milliseconds)
     */
    this.init = function () {
      let interval = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : DEFAULT_INTERVAL;
      let maxTimeout = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : DEFAULT_TIMEOUT;
      _this.interval = interval;
      _this.maxTimeout = maxTimeout;
      _this.lastActiveTime = Date.now();
      _this._triggerTimer();
      _this._bindEvents();
    };
    /**
     * @description Temporarily suspends the idle check iteration
     */
    this.pause = () => {
      window.clearTimeout(this.idleTimer);
      this._unbindEvents();
    };
    /**
     * @description Resets the last active time and resumes the idle check iteration
     */
    this.resume = () => {
      this.lastActiveTime = Date.now();
      this._triggerTimer();
      this._bindEvents();
    };
    /* Private functions */
    /**
     * @description Binds the events like keydown/mouse actions to identify the user is active
     */
    this._bindEvents = () => {
      DEFAULT_EVENTS.forEach(event => {
        document.addEventListener(event, this._handleEvent);
      });
    };
    /**
     * @description Stops listening the keydown/mouse events
     */
    this._unbindEvents = () => {
      DEFAULT_EVENTS.forEach(event => {
        document.removeEventListener(event, this._handleEvent);
      });
    };
    /**
     * @description Whenever any user action happens store the last active time as current
     * If the page was idle till now trigger the "active" event
     */
    this._handleEvent = () => {
      if (this.isIdle) {
        this.emit('active');
        this._triggerTimer();
      }
      this.isIdle = false;
      this.lastActiveTime = Date.now();
    };
    this._triggerTimer = () => {
      window.clearTimeout(this.idleTimer);
      this.idleTimer = window.setTimeout(this._checkIdle, this.interval);
    };
    /**
     * @description Triggers "idle" event when the idle time is more than maximum timeout
     * Else continues to do the same check after certain interval
     */
    this._checkIdle = () => {
      const elapsedTime = Date.now() - this.lastActiveTime;
      if (!this.isIdle && elapsedTime >= this.maxTimeout) {
        this.isIdle = true;
        this.emit('idle');
      } else {
        this._triggerTimer();
      }
    };
    this.isIdle = false;
    this.idleTimer = null;
    this.interval = DEFAULT_INTERVAL;
    this.maxTimeout = DEFAULT_TIMEOUT;
    this.lastActiveTime = Date.now();
  }
}
export default new IdleTimer();